import { EntityType, ENTITY_FIELDS, FieldConfig } from '@/hooks/useImport';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface ImportMapperProps {
  headers: string[];
  entityType: EntityType;
  mapping: Record<string, string>;
  onMappingChange: (csvColumn: string, entityField: string | null) => void;
}

export function ImportMapper({ headers, entityType, mapping, onMappingChange }: ImportMapperProps) {
  const fields = ENTITY_FIELDS[entityType];
  const mappedFields = new Set(Object.values(mapping));

  // Get unmapped required fields for warning
  const unmappedRequired = fields.filter(f => f.required && !mappedFields.has(f.key));

  const getFieldLabel = (field: FieldConfig) => {
    return field.required ? `${field.label} *` : field.label;
  };

  return (
    <div className="space-y-4">
      {/* Warning banner for unmapped required fields */}
      {unmappedRequired.length > 0 && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
          <div className="flex items-start gap-2">
            <svg
              className="w-4 h-4 text-amber-600 mt-0.5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <p className="text-sm font-medium text-amber-800">Required fields not mapped</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Please map: {unmappedRequired.map(f => f.label).join(', ')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mapping grid */}
      <div className="space-y-2">
        <div className="grid grid-cols-[1fr,auto,1fr] gap-2 px-1 text-xs font-medium text-muted-foreground">
          <span>CSV Column</span>
          <span></span>
          <span>Map to Field</span>
        </div>

        {headers.map(header => {
          const currentMapping = mapping[header];
          const currentField = fields.find(f => f.key === currentMapping);

          return (
            <div key={header} className="grid grid-cols-[1fr,auto,1fr] gap-2 items-center">
              {/* CSV Column name */}
              <div className="px-3 py-2 bg-muted/50 rounded-md border border-border">
                <span className="text-sm font-mono truncate block">{header}</span>
              </div>

              {/* Arrow indicator */}
              <div className="flex items-center justify-center w-6">
                <svg
                  className={cn(
                    'w-4 h-4',
                    currentMapping ? 'text-green-500' : 'text-muted-foreground/50'
                  )}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </div>

              {/* Field selector */}
              <Select
                value={currentMapping || ''}
                onChange={e => {
                  const value = e.target.value;
                  onMappingChange(header, value === '' ? null : value);
                }}
                className={cn(currentField?.required && 'border-green-300 bg-green-50/50')}
              >
                <option value="">Don't import</option>
                {fields.map(field => {
                  const isAlreadyMapped =
                    mappedFields.has(field.key) && currentMapping !== field.key;
                  return (
                    <option key={field.key} value={field.key} disabled={isAlreadyMapped}>
                      {getFieldLabel(field)}
                      {isAlreadyMapped ? ' (mapped)' : ''}
                    </option>
                  );
                })}
              </Select>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="pt-2 border-t border-border">
        <p className="text-xs text-muted-foreground">
          <span className="text-amber-600">*</span> Required fields must be mapped to continue
        </p>
      </div>
    </div>
  );
}
